import { ProgressModal } from "@/components/Elements/Feedback/ProgressModal";
import { InputLabel } from "@/components/Elements/Texts/InputLabel";
import { useBuildingAutocomplete } from "@/features/LG/components/Page1/components/BuildingName/api/useBuildingAutocomplete";
import { useBuildingDetail } from "@/features/LG/components/Page1/components/BuildingName/api/useBuildingDetail";
import { DisplaySelector } from "@/features/LG/components/Page2/shared/DisplaySelector";
import { useDebounce } from "@/hooks/useDebounce";
import {
  buildingStructureState,
  buildingYearState,
  Page1State,
  totalFloorState,
} from "@/store/propertyState";
import { Autocomplete, Box, ListItemText, TextField } from "@mui/material";
import { useEffect } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { useSetRecoilState } from "recoil";

interface Props {
  form: UseFormReturn<any, any>;
}

export const PropertyNameForm: React.FC<Props> = ({ form }) => {
  // global state
  const suggest = useBuildingAutocomplete();
  const info = useBuildingDetail();

  // complete state
  const setPageValue = useSetRecoilState(Page1State);
  const setBuildingYear = useSetRecoilState(buildingYearState);
  const setBuildingStructure = useSetRecoilState(buildingStructureState);
  const setTotalFloor = useSetRecoilState(totalFloorState);

  // form state
  const { setValue, control, watch } = form;
  const propertyInput = watch("propertyName");

  // debouncedで0.5秒の変更猶予を設けて実行
  const debouncedPropertyName = useDebounce<string>(propertyInput, 500);
  useEffect(() => {
    if (debouncedPropertyName.length <= 2) {
      suggest.reset();
    } else {
      suggest.fetch({
        building_name: debouncedPropertyName,
      });
    }
  }, [debouncedPropertyName]);

  return (
    <>
      <DisplaySelector name="apartment">
        <ProgressModal open={info.isLoading} />
        <div>
          <InputLabel required label="マンション名" />
          <Controller
            name="propertyName"
            control={control}
            render={({ field, fieldState }) => {
              return (
                <Autocomplete
                  ref={field.ref}
                  value={field.value}
                  freeSolo
                  fullWidth
                  onInputChange={(e, newValue) => {
                    setValue("propertyName", newValue);
                  }}
                  options={suggest.result ? suggest.result : []}
                  onChange={(_, newValue: any) => {
                    if (!newValue) return;
                    info.fetch(
                      {
                        address: newValue.value,
                      },
                      (r) => {
                        // フォーム更新
                        setPageValue({
                          propertyName: newValue.label,
                          postCode: r.postcode,
                          locationArea: r.addrea_area,
                          locationDetail: r.addrea_detail,
                        });
                        // ステート更新
                        setBuildingYear(String(r.built_year));
                        setBuildingStructure(r.structure);
                        setTotalFloor(String(r.aboveground_floor));
                      }
                    );
                  }}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        placeholder="〇〇マンション"
                      />
                    );
                  }}
                  renderOption={(
                    props,
                    option: { label: string; value: string }
                  ) => (
                    <Box component="li" {...props}>
                      <ListItemText
                        primary={option.label}
                        secondary={option.value}
                      />
                    </Box>
                  )}
                />
              );
            }}
          />
        </div>
      </DisplaySelector>
    </>
  );
};
